import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Icon from '../../components/AppIcon';
import { listMyInstitutionAssignments, startInstitutionAssignment, getApiBaseUrl } from '../../utils/api';
import { getCacheManager } from '../../utils/cache-manager';

const INSTITUTION_ASSIGNMENTS_ENDPOINT = '/api/institution-assignments';

// Resolve an API-relative logo path (e.g. "/api/institution/public/iitk/logo?v=…")
// to an absolute URL the browser can load. Passes through empty/absolute values.
const resolveLogoUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${getApiBaseUrl()}${path}`;
};

// ---------------------------------------------------------------------------
// Institution B2B — student-facing "Assigned Interviews" page.
//
// Lists the interviews an institution has assigned to the signed-in student and
// launches them through the EXACT same interview flow a normal practice session
// uses (create session -> pre-interview-check -> live panel -> report). No coins
// are charged; the institution reserved the credit at assign time. The server
// derives the student from their JWT, so this page can only ever show the
// student's own assignments.
//
// Layout is a two-column master-detail: a compact overview band on top (counts +
// performance), a scrollable list of interviews on the LEFT, and the details of
// the selected interview on the RIGHT (sticky on desktop). Everything is derived
// client-side from the assignments payload the backend already returns.
// ---------------------------------------------------------------------------

const STATUS_META = {
  assigned: { label: 'Assigned', icon: 'Circle', pill: 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-primary/10 dark:text-primary dark:border-primary/20' },
  started: { label: 'In Progress', icon: 'Loader', pill: 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20' },
  completed: { label: 'Completed', icon: 'CheckCircle2', pill: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-muted dark:text-slate-300 dark:border-border' },
  expired: { label: 'Expired', icon: 'AlertCircle', pill: 'bg-red-50 text-red-600 border-red-100 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20' },
};

const TONE = {
  neutral: { bg: 'bg-slate-100 dark:bg-muted', fg: 'text-slate-500 dark:text-slate-300' },
  emerald: { bg: 'bg-emerald-50 dark:bg-primary/10', fg: 'text-emerald-600 dark:text-primary' },
  amber: { bg: 'bg-amber-50 dark:bg-amber-500/10', fg: 'text-amber-600 dark:text-amber-400' },
};

const formatDeadline = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// Compact "Mon D" date for the assigned/started/completed timeline.
const formatShortDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const isOverdue = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
};

const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

// A dependency-free SVG progress ring for the average score. Track + arc colours
// use CSS vars that already flip in dark mode, so no dark: variants are needed.
const ScoreRing = ({ value, size = 116, stroke = 10 }) => {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const hasValue = typeof value === 'number' && !Number.isNaN(value);
  const clamped = hasValue ? Math.max(0, Math.min(100, value)) : 0;
  const offset = circumference - (clamped / 100) * circumference;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} style={{ stroke: 'var(--color-border)' }} />
        {hasValue && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            style={{ stroke: 'var(--color-primary)', strokeDasharray: circumference, strokeDashoffset: offset, transition: 'stroke-dashoffset 700ms ease' }}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-semibold text-slate-900 dark:text-slate-100 leading-none tabular-nums">{hasValue ? value : '—'}</span>
        <span className="text-[10px] font-semibold tracking-wide text-slate-400 dark:text-slate-500 mt-0.5">/ 100</span>
      </div>
    </div>
  );
};

// Compact stat used in the overview band (icon chip + number + label).
const StatItem = ({ icon, tone, value, label }) => {
  const t = TONE[tone] || TONE.neutral;
  return (
    <div className="flex items-center gap-3">
      <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${t.bg}`}>
        <Icon name={icon} size={16} className={t.fg} />
      </span>
      <div className="min-w-0">
        <div className="text-lg font-semibold text-slate-900 dark:text-slate-100 leading-none tabular-nums">{value}</div>
        <div className="mt-1 text-[11px] font-medium text-slate-500 dark:text-slate-400 truncate">{label}</div>
      </div>
    </div>
  );
};

const Readout = ({ icon, label, value }) => (
  <div className="rounded-xl bg-slate-50 dark:bg-muted/40 border border-slate-100 dark:border-border px-2.5 py-2 text-center">
    <Icon name={icon} size={13} className="mx-auto text-slate-400 dark:text-slate-500" />
    <div className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100 leading-none tabular-nums">{value ?? '—'}</div>
    <div className="mt-0.5 text-[9px] font-semibold tracking-wide text-slate-400 dark:text-slate-500 ">{label}</div>
  </div>
);

// A single selectable row in the LEFT master list.
const InterviewRow = ({ a, selected, onSelect }) => {
  const meta = STATUS_META[a.status] || STATUS_META.assigned;
  const deadline = formatDeadline(a.dueAt);
  const overdue = a.status === 'assigned' && isOverdue(a.dueAt);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left rounded-2xl border p-3 transition-all ${
        selected
          ? 'border-emerald-300 bg-emerald-50/50 ring-1 ring-emerald-200 dark:border-primary/40 dark:bg-primary/10 dark:ring-primary/20'
          : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50/60 dark:border-border dark:bg-card dark:hover:border-border/80'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wide border ${meta.pill}`}>
              <Icon name={meta.icon} size={9} className={a.status === 'started' ? 'animate-spin' : ''} />
              {meta.label}
            </span>
            {a.status === 'completed' && typeof a.score === 'number' && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 dark:bg-primary/10 dark:text-primary dark:border-primary/20">
                <Icon name="Award" size={9} /> {a.score}
              </span>
            )}
          </div>
          <h4 className="text-[13px] font-semibold text-slate-900 dark:text-slate-100 truncate">{a.campaignName}</h4>
          <div className="flex items-center gap-x-2.5 gap-y-0.5 flex-wrap mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            <span className="inline-flex items-center gap-1">
              <Icon name="Layers" size={10} /> {a.interviewType}
            </span>
            <span className="inline-flex items-center gap-1">
              <Icon name="Clock" size={10} /> {a.duration}m
            </span>
            {deadline && (
              <span className={`inline-flex items-center gap-1 ${overdue ? 'text-red-500 dark:text-red-400 font-semibold' : ''}`}>
                <Icon name="Calendar" size={10} /> {overdue ? 'Past due' : deadline}
              </span>
            )}
          </div>
        </div>
        <Icon
          name="ChevronRight"
          size={16}
          className={`mt-1 shrink-0 ${selected ? 'text-emerald-500 dark:text-primary' : 'text-slate-300 dark:text-slate-600'}`}
        />
      </div>
    </button>
  );
};

// A labelled fact in the RIGHT detail panel's meta grid.
const Field = ({ icon, label, value, highlight }) => (
  <div>
    <div className="flex items-center gap-1 text-[10px] font-semibold  tracking-wide text-slate-400 dark:text-slate-500">
      <Icon name={icon} size={11} /> {label}
    </div>
    <div className={`mt-0.5 text-xs sm:text-sm font-medium ${highlight ? 'text-red-500 dark:text-red-400' : 'text-slate-800 dark:text-slate-200'}`}>
      {value || '—'}
    </div>
  </div>
);

// Live countdown to an assignment's due date. Shown while the interview is still
// "assigned" (not yet started) so the student can see how long they have left to
// begin. Isolated as its own component so its 1s tick only re-renders the timer,
// not the whole detail panel.
const CountdownTimer = ({ dueAt, compact = false }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const target = new Date(dueAt).getTime();
  if (Number.isNaN(target)) return null;
  const diff = target - now;
  const overdue = diff <= 0;
  const abs = Math.abs(diff);
  const d = Math.floor(abs / 86400000);
  const h = Math.floor((abs % 86400000) / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const text = d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
  const urgent = !overdue && abs < 86400000; // under a day left

  if (compact) {
    const tone = overdue
      ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border-red-200/80 dark:border-red-500/20'
      : urgent
      ? 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border-amber-200/80 dark:border-amber-500/20'
      : 'text-emerald-700 dark:text-primary bg-emerald-50 dark:bg-primary/10 border-emerald-200/80 dark:border-primary/20';
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold ${tone}`}>
        <Icon name={overdue ? 'AlarmClock' : 'Timer'} size={14} className={overdue ? '' : 'animate-pulse shrink-0'} />
        <div className="flex items-center gap-1.5 leading-none">
          <span className="text-[10px]  font-bold tracking-wider opacity-75">{overdue ? 'Past due:' : 'Ends in:'}</span>
          <span className="tabular-nums font-bold text-xs">{text}</span>
        </div>
      </div>
    );
  }

  const tone = overdue
    ? 'bg-red-50 border-red-100 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400'
    : urgent
    ? 'bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-500/10 dark:border-amber-500/20 dark:text-amber-400'
    : 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-primary/10 dark:border-primary/20 dark:text-primary';
  return (
    <div className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 ${tone}`}>
      <Icon name={overdue ? 'AlarmClock' : 'Timer'} size={18} className={overdue ? '' : 'animate-pulse'} />
      <div className="min-w-0">
        <div className="text-[10px] font-semibold  tracking-wide opacity-80">
          {overdue ? 'Deadline passed' : 'Time left to start'}
        </div>
        <div className="text-base font-semibold tabular-nums leading-tight">{overdue ? `Past due by ${text}` : text}</div>
      </div>
    </div>
  );
};

// Horizontal 3-step progress: Assigned —— Started —— Completed. Reached steps are
// coloured and dated; the connector to a step lights up once that step is reached.
const TimelineStepper = ({ assignedDate, startedDate, completedDate }) => {
  const steps = [
    { key: 'assigned', label: 'Assigned', icon: 'CalendarDays', date: assignedDate, reached: !!assignedDate },
    { key: 'started', label: 'Started', icon: 'Play', date: startedDate, reached: !!startedDate },
    { key: 'completed', label: 'Completed', icon: 'CheckCircle2', date: completedDate, reached: !!completedDate },
  ];
  return (
    <div className="flex items-start">
      {steps.map((step, i) => (
        <React.Fragment key={step.key}>
          <div className="flex flex-col items-center w-14 shrink-0">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center ${
                step.reached
                  ? 'bg-emerald-50 text-emerald-600 dark:bg-primary/15 dark:text-primary'
                  : 'bg-slate-100 text-slate-300 dark:bg-muted dark:text-slate-500'
              }`}
            >
              <Icon name={step.icon} size={12} />
            </div>
            <span
              className={`mt-1.5 text-[10px] font-semibold text-center ${
                step.reached ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500'
              }`}
            >
              {step.label}
            </span>
            <span className="mt-0.5 h-3 text-[9px] text-slate-400 dark:text-slate-500 tabular-nums">{step.date || ''}</span>
          </div>
          {i < steps.length - 1 && (
            <div
              className={`flex-1 h-0.5 mt-3.5 rounded-full ${
                steps[i + 1].reached ? 'bg-emerald-400 dark:bg-primary/50' : 'bg-slate-200 dark:bg-border'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const AssignedInterviews = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startingId, setStartingId] = useState(null);
  const [actionError, setActionError] = useState({ id: null, message: '' });
  const [selectedId, setSelectedId] = useState(null);
  const detailRef = useRef(null);

  const applyAssignments = useCallback((data) => {
    setAssignments(Array.isArray(data?.assignments) ? data.assignments : []);
  }, []);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listMyInstitutionAssignments();
      applyAssignments(data);
    } catch (err) {
      setError(err?.error || 'We could not load your assigned interviews. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [applyAssignments]);

  useEffect(() => {
    // The first call returns IndexedDB data immediately when available and
    // starts a network revalidation. Subscribe before it so fresh API data
    // updates this page as soon as the shared cache is written.
    const unsubscribe = getCacheManager().subscribe(
      INSTITUTION_ASSIGNMENTS_ENDPOINT,
      {},
      (freshData) => {
        applyAssignments(freshData);
        setError('');
        setLoading(false);
      },
    );
    loadAssignments();
    return unsubscribe;
  }, [applyAssignments, loadAssignments]);

  // Keep a valid selection: default to the first assignment, and preserve the
  // current one across refreshes when it still exists.
  useEffect(() => {
    if (!assignments.length) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => (prev && assignments.some((a) => a.id === prev) ? prev : assignments[0].id));
  }, [assignments]);

  const selected = useMemo(() => assignments.find((a) => a.id === selectedId) || null, [assignments, selectedId]);

  // Roll-up counts + score aggregates, all derived from the assignments list.
  const stats = useMemo(() => {
    let completed = 0;
    let started = 0;
    let assigned = 0;
    let expired = 0;
    const scored = []; // { score, at } for completed interviews that have a numeric score
    for (const a of assignments) {
      switch (a.status) {
        case 'completed':
          completed += 1;
          if (typeof a.score === 'number') scored.push({ score: a.score, at: a.completedAt });
          break;
        case 'started':
          started += 1;
          break;
        case 'assigned':
          assigned += 1;
          break;
        case 'expired':
          expired += 1;
          break;
        default:
          break;
      }
    }
    const byTime = [...scored].sort((x, y) => new Date(x.at || 0) - new Date(y.at || 0));
    const avg = scored.length ? Math.round(scored.reduce((sum, s) => sum + s.score, 0) / scored.length) : null;
    const best = scored.length ? Math.max(...scored.map((s) => s.score)) : null;
    const latest = byTime.length ? byTime[byTime.length - 1].score : null;
    return {
      total: assignments.length,
      completed,
      started,
      assigned,
      expired,
      pending: assigned + started,
      avg,
      best,
      latest,
      completionPct: pct(completed, assignments.length),
    };
  }, [assignments]);

  // Group assignments under the institution that assigned them (usually one).
  const groups = useMemo(() => {
    const map = new Map();
    for (const a of assignments) {
      const key = a.institutionName || 'Your institution';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    }
    return Array.from(map.entries()).map(([name, items]) => ({
      name,
      items,
      completed: items.filter((i) => i.status === 'completed').length,
      total: items.length,
    }));
  }, [assignments]);

  // Selecting a row on mobile (single column) scrolls the detail into view so
  // the tap has a visible effect; on desktop the detail is always in view.
  const handleSelect = (id) => {
    setSelectedId(id);
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  };

  // Launch (or resume) an assignment through the shared interview flow.
  const handleStart = async (assignment) => {
    if (startingId) return;
    setStartingId(assignment.id);
    setActionError({ id: null, message: '' });
    try {
      const res = await startInstitutionAssignment(assignment.id);
      const sessionId = res?.session_id;
      if (!sessionId) throw { error: 'Could not start the interview. Please try again.' };

      // Mirror the exact shape interview-setup hands to the interview flow so the
      // pre-interview-check -> live-panel -> report pipeline behaves identically.
      const sessionConfig = {
        company: assignment.company || 'General',
        role: assignment.role || assignment.campaignName || 'Candidate',
        interviewType: assignment.interviewType,
        type: assignment.interviewType,
        duration: assignment.duration,
        difficulty: assignment.difficulty,
        persona: 'friendly',
        coinsDeducted: 0,
        coinsPerMinute: 0,
        isPlanSession: false,
        isChallenge: false,
        isInstitutionAssignment: true,
        institutionName: assignment.institutionName,
        institutionLogoUrl: resolveLogoUrl(assignment.institutionLogoUrl),
        instructions: assignment.instructions || '',
        campaignName: assignment.campaignName,
      };

      localStorage.setItem(
        'currentInterviewSession',
        JSON.stringify({ ...sessionConfig, startTime: new Date().toISOString(), sessionId }),
      );
      navigate('/pre-interview-check', { state: { sessionId, config: sessionConfig, isChallenge: false } });
    } catch (err) {
      setActionError({ id: assignment.id, message: err?.error || 'Could not start the interview. Please try again.' });
      setStartingId(null);
      // A stale list is the usual cause of a 409 (already completed / expired) — refresh it.
      loadAssignments();
    }
  };

  const handleViewReport = (assignment) => {
    if (!assignment.sessionId) return;
    navigate(`/interview-report?session=${encodeURIComponent(assignment.sessionId)}`);
  };

  // Clean, appropriately sized primary action for the detail panel.
  const renderDetailAction = (a) => {
    const busy = startingId === a.id;
    if (a.status === 'completed') {
      return (
        <button
          onClick={() => handleViewReport(a)}
          disabled={!a.sessionId}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white dark:bg-emerald-600 dark:hover:bg-emerald-500 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Icon name="FileText" size={15} />
          View Report
        </button>
      );
    }
    if (a.status === 'expired') {
      return (
        <div className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 text-slate-400 border border-slate-200 dark:bg-[#1a1b21] dark:text-slate-500 dark:border-border cursor-not-allowed">
          <Icon name="AlertCircle" size={14} />
          Expired
        </div>
      );
    }
    const resuming = a.status === 'started';
    return (
      <button
        onClick={() => handleStart(a)}
        disabled={busy}
        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white dark:bg-emerald-600 dark:hover:bg-emerald-500 shadow-md shadow-emerald-600/20 dark:shadow-none transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-wait disabled:hover:scale-100"
      >
        {busy ? (
          <>
            <Icon name="Loader" size={15} className="animate-spin" />
            {resuming ? 'Resuming…' : 'Starting…'}
          </>
        ) : (
          <>
            <Icon name={resuming ? 'RotateCcw' : 'Play'} size={15} className={resuming ? '' : 'fill-white'} />
            {resuming ? 'Resume Interview' : 'Start Interview'}
          </>
        )}
      </button>
    );
  };

  // The RIGHT panel: everything about the selected interview.
  const renderDetail = (a) => {
    const meta = STATUS_META[a.status] || STATUS_META.assigned;
    const deadline = formatDeadline(a.dueAt);
    const overdue = a.status === 'assigned' && isOverdue(a.dueAt);
    const topics = Array.isArray(a.topics) ? a.topics.filter(Boolean) : [];
    const assignedDate = formatShortDate(a.assignedAt);
    const startedDate = formatShortDate(a.startedAt);
    const completedDate = formatShortDate(a.completedAt);
    return (
      <div className="bg-white dark:bg-card border border-slate-100 dark:border-border rounded-3xl p-5 sm:p-6 shadow-sm">
        {/* Header: Institution badge + status + score */}
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600 dark:bg-muted dark:text-slate-300">
            <Icon name="Building2" size={12} /> {a.institutionName || 'Your institution'}
          </span>
          <div className="flex items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide border ${meta.pill}`}>
              <Icon name={meta.icon} size={10} className={a.status === 'started' ? 'animate-spin' : ''} />
              {meta.label}
            </span>
            {a.status === 'completed' && typeof a.score === 'number' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 dark:bg-primary/10 dark:text-primary dark:border-primary/20">
                <Icon name="Award" size={10} /> {a.score}/100
              </span>
            )}
          </div>
        </div>

        {/* Title & Subtitle */}
        <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{a.campaignName}</h2>
        {(a.role || a.company) && (
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {[a.role, a.company].filter(Boolean).join(' · ')}
          </p>
        )}

        {/* Compact Meta specs (Type, Duration, Difficulty, Due Date) */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="bg-slate-50/90 dark:bg-[#1a1b21] border border-slate-100 dark:border-border/60 rounded-xl px-3 py-2">
            <div className="flex items-center gap-1 text-[10px] font-semibold tracking-wider text-slate-400 dark:text-slate-500">
              <Icon name="Layers" size={11} /> Type
            </div>
            <div className="mt-0.5 text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 capitalize truncate">
              {a.interviewType || '—'}
            </div>
          </div>
          <div className="bg-slate-50/90 dark:bg-[#1a1b21] border border-slate-100 dark:border-border/60 rounded-xl px-3 py-2">
            <div className="flex items-center gap-1 text-[10px] font-semibold tracking-wider text-slate-400 dark:text-slate-500">
              <Icon name="Clock" size={11} /> Duration
            </div>
            <div className="mt-0.5 text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
              {a.duration != null ? `${a.duration} mins` : '—'}
            </div>
          </div>
          <div className="bg-slate-50/90 dark:bg-[#1a1b21] border border-slate-100 dark:border-border/60 rounded-xl px-3 py-2">
            <div className="flex items-center gap-1 text-[10px] font-semibold tracking-wider text-slate-400 dark:text-slate-500">
              <Icon name="BarChart2" size={11} /> Difficulty
            </div>
            <div className="mt-0.5 text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 capitalize truncate">
              {a.difficulty || '—'}
            </div>
          </div>
          <div className="bg-slate-50/90 dark:bg-[#1a1b21] border border-slate-100 dark:border-border/60 rounded-xl px-3 py-2">
            <div className="flex items-center gap-1 text-[10px] font-semibold tracking-wider text-slate-400 dark:text-slate-500">
              <Icon name="Calendar" size={11} /> Due Date
            </div>
            <div className={`mt-0.5 text-xs sm:text-sm font-semibold truncate ${overdue ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-slate-200'}`}>
              {deadline || 'No deadline'}
            </div>
          </div>
        </div>

        {/* Instructions from the institution (or guidelines) — PROMINENT AND VISIBLE ABOVE THE FOLD */}
        <div className="mt-4 p-4 rounded-2xl bg-amber-50/60 dark:bg-amber-500/10 border border-amber-200/70 dark:border-amber-500/20">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center text-amber-800 dark:text-amber-300 shrink-0">
              <Icon name="FileText" size={13} />
            </div>
            <span className="text-xs font-bold tracking-tight text-amber-950 dark:text-amber-200">
              {a.instructions ? `Instructions from ${a.institutionName || 'Institution'}` : 'Important Assessment Guidelines'}
            </span>
          </div>
          {a.instructions ? (
            <p className="text-[13px] leading-relaxed text-slate-700 dark:text-slate-300 whitespace-pre-line pl-8">
              {a.instructions}
            </p>
          ) : (
            <ul className="text-[12px] leading-relaxed text-slate-600 dark:text-slate-400 space-y-1 pl-8 list-disc list-outside">
              <li>Ensure a quiet environment with a reliable internet connection.</li>
              <li>Camera and microphone are required for the AI assessment environment.</li>
              <li>Once you click <strong>Start Interview</strong>, your session timer will begin.</li>
            </ul>
          )}
        </div>

        {/* Unified Action & Countdown Bar */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-[#1a1b21] border border-slate-100 dark:border-border">
          <div className="flex items-center gap-2 min-w-0">
            {a.status === 'assigned' && a.dueAt && <CountdownTimer dueAt={a.dueAt} compact />}
            {a.status === 'started' && (
              <span className="text-xs text-amber-700 dark:text-amber-400 font-medium inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200/50">
                <Icon name="Clock" size={13} /> Session in progress
              </span>
            )}
            {a.status === 'completed' && (
              <span className="text-xs text-slate-600 dark:text-slate-400 font-medium inline-flex items-center gap-1.5 px-2 py-1">
                <Icon name="CheckCircle2" size={14} className="text-emerald-500" /> Completed on {completedDate || 'record'}
              </span>
            )}
            {a.status === 'expired' && (
              <span className="text-xs text-red-600 dark:text-red-400 font-medium inline-flex items-center gap-1.5 px-2 py-1">
                <Icon name="AlertCircle" size={14} /> Deadline passed
              </span>
            )}
          </div>
          <div className="shrink-0 flex items-center justify-end">{renderDetailAction(a)}</div>
        </div>

        {actionError.id === a.id && actionError.message && (
          <p className="mt-2 text-[12px] font-medium text-red-500 dark:text-red-400 inline-flex items-center gap-1.5">
            <Icon name="AlertCircle" size={12} /> {actionError.message}
          </p>
        )}

        {/* Timeline & Topics — compact bottom section */}
        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          {(assignedDate || startedDate || completedDate) && (
            <div>
              <div className="text-[10px] font-semibold  tracking-wider text-slate-400 dark:text-slate-500 mb-2">Timeline</div>
              <TimelineStepper assignedDate={assignedDate} startedDate={startedDate} completedDate={completedDate} />
            </div>
          )}

          {topics.length > 0 && (
            <div className="sm:text-right">
              <div className="text-[10px] font-semibold  tracking-wider text-slate-400 dark:text-slate-500 mb-2">Topics</div>
              <div className="flex items-center sm:justify-end gap-1.5 flex-wrap">
                {topics.map((t, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-50 text-slate-600 border border-slate-100 dark:bg-muted dark:text-slate-300 dark:border-border"
                  >
                    <Icon name="Tag" size={9} /> {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Assigned Interviews - prepxai</title>
      </Helmet>
      <div className="min-h-screen bg-white dark:bg-[#08080a] text-slate-900 dark:text-slate-200 relative overflow-x-hidden">
        {/* Premium header wash — matches the interview setup surface */}
        <div className="absolute top-0 left-0 right-0 h-[360px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white dark:from-primary/10 dark:via-transparent dark:to-transparent pointer-events-none z-0" />
        <NavigationHeader />

        <main className="pt-20 pb-20 px-6 relative z-10 institution-scope">
          <div className="max-w-6xl mx-auto">
            {/* Page heading */}
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-slate-900 dark:bg-primary flex items-center justify-center shrink-0">
                <Icon name="ClipboardList" size={20} color="#FFFFFF" className="dark:text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Assigned Interviews</h1>
                <p className="text-xs sm:text-sm font-normal text-slate-500 dark:text-slate-400 mt-0.5">
                  Interviews your institution has assigned to you. Your institution has already covered the cost.
                </p>
              </div>
            </div>

            {/* Loading skeleton */}
            {loading && (
              <div className="space-y-5">
                <div className="bg-white dark:bg-card border border-slate-100 dark:border-border rounded-2xl p-4 sm:p-5 animate-pulse">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-muted shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <div className="h-4 bg-slate-100 dark:bg-muted rounded w-1/2" />
                          <div className="h-2.5 bg-slate-100 dark:bg-muted rounded w-2/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                  <div className="lg:col-span-5 space-y-2.5">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="bg-white dark:bg-card border border-slate-100 dark:border-border rounded-2xl p-3.5 animate-pulse">
                        <div className="h-3 bg-slate-100 dark:bg-muted rounded w-1/4 mb-2" />
                        <div className="h-4 bg-slate-100 dark:bg-muted rounded w-2/3" />
                        <div className="h-2.5 bg-slate-100 dark:bg-muted rounded w-1/2 mt-2" />
                      </div>
                    ))}
                  </div>
                  <div className="lg:col-span-7">
                    <div className="bg-white dark:bg-card border border-slate-100 dark:border-border rounded-3xl p-6 animate-pulse space-y-4">
                      <div className="h-5 bg-slate-100 dark:bg-muted rounded w-1/2" />
                      <div className="grid grid-cols-4 gap-2 pt-2">
                        {[0, 1, 2, 3].map((i) => (
                          <div key={i} className="h-10 bg-slate-100 dark:bg-muted rounded" />
                        ))}
                      </div>
                      <div className="h-16 bg-slate-100 dark:bg-muted rounded-xl w-full" />
                      <div className="h-11 bg-slate-100 dark:bg-muted rounded-xl w-full" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error state */}
            {!loading && error && (
              <div className="bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20 rounded-3xl p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertCircle" size={22} className="text-red-500 dark:text-red-400" />
                </div>
                <p className="text-sm font-medium text-red-700 dark:text-red-300">{error}</p>
                <button
                  onClick={loadAssignments}
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold tracking-wide bg-white dark:bg-card border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all"
                >
                  <Icon name="RefreshCw" size={14} />
                  Try Again
                </button>
              </div>
            )}

            {/* Empty state */}
            {!loading && !error && assignments.length === 0 && (
              <div className="bg-white dark:bg-card border border-slate-100 dark:border-border rounded-3xl p-12 text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-primary/10 flex items-center justify-center mx-auto mb-5">
                  <Icon name="ClipboardCheck" size={26} className="text-emerald-500 dark:text-primary" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">No assigned interviews yet</h3>
                <p className="text-sm font-normal text-slate-500 dark:text-slate-400 mt-1.5 max-w-md mx-auto">
                  When your institution assigns you an interview, it will show up here. In the meantime, you can practice on your own from the Practice tab.
                </p>
                <button
                  onClick={() => navigate('/interview-setup')}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-bold tracking-wide bg-emerald-600 hover:bg-emerald-500 text-white dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90 shadow-lg shadow-emerald-600/20 dark:shadow-none transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Icon name="Cpu" size={14} />
                  Practice on my own
                </button>
              </div>
            )}

            {/* Overview band + two-column master-detail */}
            {!loading && !error && assignments.length > 0 && (
              <>
                {/* Overview band: counts on the left, performance on the right */}
                <div className="bg-white dark:bg-card border border-slate-100 dark:border-border rounded-2xl p-4 sm:p-5 mb-5 shadow-sm">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                    <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                      <StatItem icon="ClipboardList" tone="neutral" value={stats.total} label="Assigned" />
                      <StatItem icon="CheckCircle2" tone="emerald" value={stats.completed} label="Completed" />
                      <StatItem icon="Clock" tone="amber" value={stats.pending} label="Pending" />
                      <StatItem icon="Gauge" tone="emerald" value={stats.avg ?? '—'} label="Avg score" />
                    </div>
                    {stats.completed > 0 ? (
                      <div className="flex items-center gap-4 lg:pl-6 lg:border-l lg:border-slate-100 dark:lg:border-border shrink-0">
                        <ScoreRing value={stats.avg} size={80} stroke={8} />
                        <div className="grid grid-cols-3 gap-2">
                          <Readout icon="Trophy" label="Best" value={stats.best} />
                          <Readout icon="TrendingUp" label="Latest" value={stats.latest} />
                          <Readout icon="Target" label="Done" value={`${stats.completionPct}%`} />
                        </div>
                      </div>
                    ) : (
                      <div className="hidden lg:flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-50 dark:bg-[#1a1b21] text-xs text-slate-500 dark:text-slate-400 border border-slate-100 dark:border-border shrink-0">
                        <Icon name="ShieldCheck" size={16} className="text-emerald-600 dark:text-primary shrink-0" />
                        <span>Institution Sponsored Assessment</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Two-column: LEFT list (5 cols), RIGHT detail (7 cols) */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                  {/* LEFT — master list, grouped by institution */}
                  <div className="lg:col-span-5 space-y-4">
                    {groups.map((group) => (
                      <div key={group.name}>
                        <div className="flex items-center justify-between gap-3 mb-2.5 px-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-lg bg-slate-900 dark:bg-primary/15 flex items-center justify-center shrink-0">
                              <Icon name="Building2" size={14} className="text-white dark:text-primary" />
                            </div>
                            <div className="min-w-0">
                              <h2 className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{group.name}</h2>
                              <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                {group.completed} of {group.total} completed
                              </p>
                            </div>
                          </div>
                          <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 tabular-nums shrink-0">
                            {pct(group.completed, group.total)}%
                          </span>
                        </div>
                        <div className="space-y-2">
                          {group.items.map((a) => (
                            <InterviewRow key={a.id} a={a} selected={a.id === selectedId} onSelect={() => handleSelect(a.id)} />
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Helpful institutional placement notice to gracefully fill whitespace */}
                    <div className="p-3.5 rounded-2xl border border-dashed border-slate-200 dark:border-border bg-slate-50/60 dark:bg-[#131418] text-slate-500 dark:text-slate-400 text-xs flex items-start gap-2.5">
                      <Icon name="Building2" size={15} className="mt-0.5 text-slate-400 dark:text-slate-500 shrink-0" />
                      <p className="leading-relaxed">
                        These interviews are assigned by your institution. Completed results are automatically recorded and reported back to your placement coordinators.
                      </p>
                    </div>
                  </div>

                  {/* RIGHT — detail for the selected interview (sticky on desktop) */}
                  <div className="lg:col-span-7" ref={detailRef}>
                    <div className="lg:sticky lg:top-24">
                      {selected ? (
                        renderDetail(selected)
                      ) : (
                        <div className="bg-white dark:bg-card border border-slate-100 dark:border-border rounded-3xl p-12 text-center">
                          <div className="w-12 h-12 rounded-2xl bg-slate-50 dark:bg-muted flex items-center justify-center mx-auto mb-4">
                            <Icon name="MousePointerClick" size={22} className="text-slate-400 dark:text-slate-500" />
                          </div>
                          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Select an interview to see its details.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AssignedInterviews;
